import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import moment from 'moment';
// @ts-ignore
import ReactDatetime from 'react-datetime';

interface DatePickerInputProps {
  value?: string | null;
  onChange: (iso: string | null) => void;
  placeholder?: string;
  showTime?: boolean;
  className?: string;
  style?: React.CSSProperties;
  inputStyle?: React.CSSProperties;
  inputClassName?: string;
  disabled?: boolean;
  autoOpen?: boolean;
  onClose?: () => void;
}

const CALENDAR_H = 290;
const CALENDAR_W = 300;

const DatePickerInput: React.FC<DatePickerInputProps> = ({
  value,
  onChange,
  placeholder = 'Selecionar data...',
  showTime = false,
  className,
  style,
  inputStyle,
  inputClassName,
  disabled = false,
  autoOpen = false,
  onClose,
}) => {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const portalId = useRef(`dpid-${Math.random().toString(36).slice(2)}`);

  useEffect(() => {
    if (autoOpen && !disabled) {
      // setTimeout garante que o anchorRef já tem as coordenadas corretas após o mount
      const t = setTimeout(() => {
        calcPos();
        setOpen(true);
      }, 0);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const portal = document.getElementById(portalId.current);
      const target = e.target as Node;
      if (portal && portal.contains(target)) return;
      if (anchorRef.current && anchorRef.current.contains(target)) return;
      setOpen(false);
      if (onClose) onClose();
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, onClose]);

  function calcPos() {
    if (!anchorRef.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Vertical: abre abaixo se caber, senão abre acima
    const spaceBelow = vh - rect.bottom;
    const top = spaceBelow >= CALENDAR_H + 8
      ? rect.bottom + 4
      : Math.max(8, rect.top - CALENDAR_H - 4);

    // Horizontal: alinha à esquerda do anchor; se sair da tela pela direita,
    // alinha pela borda direita do anchor (como dropdown right-aligned)
    let left = rect.left;
    if (left + CALENDAR_W > vw - 8) {
      left = rect.right - CALENDAR_W;
    }
    if (left < 8) left = 8;

    setPos({ top, left });
  }

  function handleClick() {
    if (disabled) return;
    if (!open) calcPos();
    setOpen(prev => !prev);
  }

  const displayValue = value
    ? moment(value).format(showTime ? 'DD/MM/YYYY HH:mm' : 'DD/MM/YYYY')
    : '';

  return (
    <div ref={anchorRef} style={style} className={className}>
      <input
        readOnly
        value={displayValue}
        placeholder={placeholder}
        onClick={handleClick}
        disabled={disabled}
        className={inputClassName !== undefined ? inputClassName : 'form-control'}
        style={{ cursor: disabled ? 'default' : 'pointer', ...inputStyle }}
      />

      {open && pos && ReactDOM.createPortal(
        <div
          id={portalId.current}
          style={{ position: 'fixed', top: pos.top, left: pos.left, zIndex: 10000 }}
        >
          <ReactDatetime
            input={false}
            open
            locale="pt-br"
            value={value ? moment(value) : undefined}
            dateFormat="DD/MM/YYYY"
            timeFormat={showTime ? 'HH:mm' : false}
            onChange={(e: any) => {
              if (moment.isMoment(e)) {
                onChange(e.toISOString());
                if (!showTime) {
                  setOpen(false);
                  if (onClose) onClose();
                }
              }
            }}
          />
        </div>,
        document.body
      )}
    </div>
  );
};

export default DatePickerInput;
